/**
 * New Leave Request page - Form to create a new leave request
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { leaveApi } from '../api/leaveApi'
import toast from 'react-hot-toast'
import LoadingPage from '@/shared/components/LoadingPage'

const NewLeaveRequestPage = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [formData, setFormData] = useState({
    leave_type_id: '',
    start_date: '',
    end_date: '',
    reason: '',
  })

  // Fetch leave types
  const { data: leaveTypesData, isLoading: isLoadingTypes } = useQuery({
    queryKey: ['leaveTypes'],
    queryFn: () => leaveApi.getLeaveTypes(),
  })

  // Fetch balances
  const { data: balancesData } = useQuery({
    queryKey: ['leaveBalances'],
    queryFn: () => leaveApi.getLeaveBalances(),
  })

  // Create mutation
  const createMutation = useMutation({
    mutationFn: leaveApi.createLeaveRequest,
    onSuccess: () => {
      toast.success('Leave request created successfully')
      queryClient.invalidateQueries({ queryKey: ['leaveRequests'] })
      queryClient.invalidateQueries({ queryKey: ['leaveBalances'] })
      navigate('/leave/requests')
    },
    onError: (error: any) => {
      const message =
        error?.error?.message || 'Failed to create leave request'
      toast.error(message)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.leave_type_id || !formData.start_date || !formData.end_date) {
      toast.error('Please fill in all required fields')
      return
    }

    createMutation.mutate(formData)
  }

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  if (isLoadingTypes) return <LoadingPage />

  const leaveTypes = leaveTypesData?.data || []
  const balances = balancesData?.data || []

  // Find balance for selected leave type
  const selectedBalance = balances.find(
    (b) => b.leave_type_id === formData.leave_type_id
  )

  // Find selected leave type to check if it's sick leave
  const selectedLeaveType = leaveTypes.find(
    (t) => t.id === formData.leave_type_id
  )
  const isSickLeave = selectedLeaveType?.code?.toLowerCase() === 'sl'

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-0">
      <div className="mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">New Leave Request</h2>
        <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
          Submit a new leave request for approval
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-4 sm:p-6">
        {/* Leave Type */}
        <div className="mb-4 sm:mb-6">
          <label
            htmlFor="leave_type_id"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Leave Type *
          </label>
          <select
            id="leave_type_id"
            name="leave_type_id"
            value={formData.leave_type_id}
            onChange={handleChange}
            required
            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-base"
          >
            <option value="">Select leave type</option>
            {leaveTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>

          {selectedBalance && !isSickLeave && (
            <div className="mt-2 p-3 bg-red-50 rounded-lg">
              <p className="text-sm text-red-900">
                Available Balance:{' '}
                <span className="font-semibold">
                  {selectedBalance.available} days
                </span>
              </p>
            </div>
          )}
          {isSickLeave && (
            <div className="mt-2 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-900">
                Sick leave does not require balance
              </p>
            </div>
          )}
        </div>

        {/* Date Fields - Side by side on larger screens */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 sm:mb-6">
          {/* Start Date */}
          <div>
            <label
              htmlFor="start_date"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Start Date *
            </label>
            <input
              type="date"
              id="start_date"
              name="start_date"
              value={formData.start_date}
              onChange={handleChange}
              required
              {...(!isSickLeave && { min: new Date().toISOString().split('T')[0] })}
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>

          {/* End Date */}
          <div>
            <label
              htmlFor="end_date"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              End Date *
            </label>
            <input
              type="date"
              id="end_date"
              name="end_date"
              value={formData.end_date}
              onChange={handleChange}
              required
              {...(formData.start_date
                ? { min: formData.start_date }
                : !isSickLeave && { min: new Date().toISOString().split('T')[0] })}
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
        </div>
        {isSickLeave && (
          <p className="-mt-2 mb-4 text-sm text-gray-500">
            Sick leave can be backdated
          </p>
        )}

        {/* Reason */}
        <div className="mb-4 sm:mb-6">
          <label
            htmlFor="reason"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Reason (Optional)
          </label>
          <textarea
            id="reason"
            name="reason"
            value={formData.reason}
            onChange={handleChange}
            rows={3}
            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-base"
            placeholder="Enter reason for leave..."
          />
        </div>

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-4">
          <button
            type="button"
            onClick={() => navigate('/leave/requests')}
            className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="w-full sm:flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {createMutation.isPending ? 'Submitting...' : 'Submit Request'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default NewLeaveRequestPage
